import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip
} from '@mui/material';
import {
  Security as SecurityIcon,
  Warning as WarningIcon,
  BugReport as BugReportIcon,
  Build as BuildIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
  AlarmOff as AlarmOffIcon
} from '@mui/icons-material';

const SummaryCard = ({ title, value, icon, color = 'primary', subtitle, chip }) => (
  <Card elevation={2} sx={{ height: '100%' }}>
    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
        <Box sx={{ color: `${color}.main` }}>
          {React.cloneElement(icon, { fontSize: 'medium' })}
        </Box>
        {chip && (
          <Chip
            label={chip.label}
            color={chip.color}
            size="small"
            variant={chip.variant || 'filled'}
            sx={{ height: 20, fontSize: '0.65rem' }}
          />
        )}
      </Box>
      <Typography variant="h5" component="div" color={`${color}.main`} fontWeight="bold" sx={{ mb: 0.5 }}>
        {value?.toLocaleString() || 0}
      </Typography>
      <Typography variant="body2" color="text.primary" fontWeight="medium" sx={{ mb: 0.5 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const SnykSummaryCards = ({ data }) => {
  if (!data) return null;

  const cards = [
    {
      title: 'Critical',
      value: data.criticalIssues,
      icon: <WarningIcon />,
      color: 'error',
      subtitle: 'Critical severity',
      chip: {
        label: 'Priority',
        color: 'error',
        variant: 'outlined'
      }
    },
    {
      title: 'High',
      value: data.highIssues,
      icon: <WarningIcon />,
      color: 'error',
      subtitle: 'High severity',
      chip: {
        label: 'Priority',
        color: 'error',
        variant: 'outlined'
      }
    },
    {
      title: 'Medium',
      value: data.mediumIssues,
      icon: <WarningIcon />,
      color: 'warning',
      subtitle: 'Medium severity'
    },
    {
      title: 'Low',
      value: data.lowIssues,
      icon: <WarningIcon />,
      color: 'success',
      subtitle: 'Low severity'
    },
    {
      title: 'Out of SLA',
      value: data.outOfSLAIssues,
      icon: <AlarmOffIcon />,
      color: 'error',
      subtitle: 'Past due date',
      chip: {
        label: 'Overdue',
        color: 'error',
        variant: 'filled'
      }
    },
    {
      title: '7 days',
      value: data.issues7Days,
      icon: <ScheduleIcon />,
      color: 'secondary',
      subtitle: 'Introduced 7d'
    },
    {
      title: '15 days',
      value: data.issues15Days,
      icon: <ScheduleIcon />,
      color: 'secondary',
      subtitle: 'Introduced 15d'
    },
    {
      title: '30 days',
      value: data.issues30Days,
      icon: <ScheduleIcon />,
      color: 'secondary',
      subtitle: 'Introduced 30d'
    }
  ];

  return (
    <Box sx={{ mb: 3, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
      {cards.map((card, index) => (
        <Box key={index} sx={{ flex: '1 1 0', minWidth: { xs: 'calc(50% - 6px)', sm: 'calc(25% - 10px)', xl: 'calc(12.5% - 12px)' } }}>
          <SummaryCard {...card} />
        </Box>
      ))}
    </Box>
  );
};

export default SnykSummaryCards;
